import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import ReactGA from 'react-ga4';

import {
  fetchClimateData,
  updateClimateTrends,
  fetchClimateProjection,
  fetchStationData,
  getClimateAttrNameById
} from './climate-data.utils';
import { fetchHazardData, updateHazardMapData, getHazardAttrNameById } from './hazard-data.utils';
import { search } from './data.utils';

export const getDataAsync = createAsyncThunk(
  'data/getData',
  async (arg, { getState, requestId }) => {
    const { currentRequestId, loading, cachedData } = getState().data;
    const { dataType, climateOption, climateRange, hazardOption, hazardMetric, hazardRange } =
      getState().options;

    let data = {
      map: { county: {}, state: {} },
      raw: { county: {}, state: {} },
    };

    const newCachedData = JSON.parse(JSON.stringify(cachedData));

    if (loading && requestId === currentRequestId) {
      let name = dataType === 'climate' ? getClimateAttrNameById(climateOption) : getHazardAttrNameById(hazardOption);
      if (dataType === 'climate') {
        name += ` (${hazardMetric})`;
      }
      
      ReactGA.event({
        category: "User Interaction",
        action: `${dataType.slice(0,1).toUpperCase() + dataType.slice(1)} Selected`,
        label: name
      });
      
      if (dataType === 'climate') {
        if (newCachedData.climate[climateOption]) {
          data.raw = newCachedData.climate[climateOption].raw;
          data.map = newCachedData.climate[climateOption].map;
        } else {
          data.raw = await fetchClimateData(climateOption);
          data.map = updateClimateTrends(data.raw, climateRange);
          newCachedData.climate[climateOption] = { ...data };
        }
      } else if (dataType === 'hazard') {
        if (newCachedData.hazard[hazardOption]) {
          data.raw = newCachedData.hazard[hazardOption].raw;
          data.map = newCachedData.hazard[hazardOption].map;
        } else {
          data.raw = await fetchHazardData(hazardOption);
          data.map = updateHazardMapData(data.raw, hazardRange);
          newCachedData.hazard[hazardOption] = { ...data };
        }
      }
    }
    return { data, newCachedData };
  }
);

export const getSearchLocationAsync = createAsyncThunk(
  'data/getSearchLocation',
  async (arg, { getState, requestId }) => {
    const { searching, currentSearchId } = getState().data;
    let newLocation = { lat: null, lng: null, zoom: null, name: null };
    if (searching && requestId === currentSearchId) {
      newLocation = await search(arg);
    }
    return newLocation;
  }
);

export const getProjectionDataAsync = createAsyncThunk(
  'data/getProjectionData',
  async (arg, { getState, requestId }) => {
    const { currentProjReqId, projecting } = getState().data;

    let chartData = {
      veryHigh: { pastMean: [], mean: [], range: [] },
      high: { pastMean: [], mean: [], range: [] },
      intermediate: { pastMean: [], mean: [], range: [] },
    };

    let locId, attrId;
    if (arg.type === 'changeAttribute') {
      const { currentLocation } = getState().map;
      locId = currentLocation.id;
      attrId = arg.payload;
    } else if (arg.type === 'changeLocation') {
      const { climateOption } = getState().options;
      locId = arg.payload;
      attrId = climateOption;
    }

    if (locId && attrId && projecting && requestId === currentProjReqId) {
      chartData = await fetchClimateProjection(locId, attrId);
    }
    return chartData;
  }
);

export const getStationDataAsync = createAsyncThunk(
  'data/getStationData',
  async (args, { getState, requestId }) => {
    const { gettingStation, currentStnReqId } = getState().data;
    let newStnData = {
      past: [],
      projected: {
        veryHigh: {
          pastMean: [],
          mean: [],
          range: [],
        },
        high: {
          pastMean: [],
          mean: [],
          range: [],
        },
        intermediate: {
          pastMean: [],
          mean: [],
          range: [],
        },
      }
    };
    if (gettingStation && requestId === currentStnReqId) {
      newStnData = await fetchStationData(args);
    }
    return newStnData;
  }
);

const initialState = {
  loading: false,
  currentRequestId: undefined,
  searching: false,
  currentSearchId: undefined,
  projecting: false,
  currentProjReqId: undefined,
  gettingStation: false,
  currentStnReqId: undefined,
  raw: {
    county: {},
    state: {},
  },
  map: {
    county: {},
    state: {},
  },
  searchLocation: {
    lat: null,
    lng: null,
    zoom: null,
    name: null,
  },
  projected: {
    veryHigh: {
      pastMean: [],
      mean: [],
      range: [],
    },
    high: {
      pastMean: [],
      mean: [],
      range: [],
    },
    intermediate: {
      pastMean: [],
      mean: [],
      range: [],
    },
  },
  station: {
    past: [],
    projected: {
      veryHigh: {
        pastMean: [],
        mean: [],
        range: [],
      },
      high: {
        pastMean: [],
        mean: [],
        range: [],
      },
      intermediate: {
        pastMean: [],
        mean: [],
        range: [],
      },
    }
  },
  cachedData: { climate: {}, hazard: {} }
};

const dataSlice = createSlice({
  name: 'data',
  initialState,
  reducers: {
    updateMapData(state, { payload: { dateRange, isClimate } }) {
      state.map = isClimate
        ? updateClimateTrends(state.raw, dateRange)
        : updateHazardMapData(state.raw, dateRange);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getDataAsync.pending, (state, action) => {
        if (!state.loading) {
          state.loading = true;
          state.currentRequestId = action.meta.requestId;
        }
      })
      .addCase(getDataAsync.fulfilled, (state, action) => {
        const { requestId } = action.meta;
        if (state.loading && state.currentRequestId === requestId) {
          state.raw = action.payload.data.raw;
          state.map = action.payload.data.map;
          state.cachedData = action.payload.newCachedData;
          state.loading = false;
          state.currentRequestId = undefined;
        }
      })
      .addCase(getDataAsync.rejected, (state, action) => {
        const { requestId } = action.meta;
        if (state.loading && state.currentRequestId === requestId) {
          state.loading = false;
          alert(
            'An error has occurred and the data being shown may not be accurate.'
          );
          state.currentRequestId = undefined;
        }
      })
      .addCase(getSearchLocationAsync.pending, (state, action) => {
        if (!state.searching) {
          state.searching = true;
          state.currentSearchId = action.meta.requestId;
        }
      })
      .addCase(getSearchLocationAsync.fulfilled, (state, action) => {
        const { requestId } = action.meta;
        if (state.searching && state.currentSearchId === requestId) {
          state.searchLocation = action.payload;
          state.searching = false;
          state.currentSearchId = undefined;
        }
      })
      .addCase(getSearchLocationAsync.rejected, (state, action) => {
        const { requestId } = action.meta;
        if (state.searching && state.currentSearchId === requestId) {
          state.searching = false;
          alert('An error occurred while trying to find a matching location.');
          state.currentSearchId = undefined;
        }
      })
      .addCase(getProjectionDataAsync.pending, (state, action) => {
        if (!state.projecting) {
          state.projecting = true;
          state.currentProjReqId = action.meta.requestId;
        }
      })
      .addCase(getProjectionDataAsync.fulfilled, (state, action) => {
        const { requestId } = action.meta;
        if (state.projecting && state.currentProjReqId === requestId) {
          state.projected = action.payload;
          state.projecting = false;
          state.currentProjReqId = undefined;
        }
      })
      .addCase(getProjectionDataAsync.rejected, (state, action) => {
        const { requestId } = action.meta;
        if (state.projecting && state.currentProjReqId === requestId) {
          state.projecting = false;
          alert(
            'An error has occurred and the projected data being shown may not be accurate.'
          );
          state.currentProjReqId = undefined;
        }
      })
      .addCase(getStationDataAsync.pending, (state, action) => {
        if (!state.gettingStation) {
          state.gettingStation = true;
          state.currentStnReqId = action.meta.requestId;
        }
      })
      .addCase(getStationDataAsync.fulfilled, (state, action) => {
        const { requestId } = action.meta;
        if (state.gettingStation && state.currentStnReqId === requestId) {
          state.station = action.payload;
          state.gettingStation = false;
          state.currentStnReqId = undefined;
        }
      })
      .addCase(getStationDataAsync.rejected, (state, action) => {
        const { requestId } = action.meta;
        if (state.gettingStation && state.currentStnReqId === requestId) {
          state.gettingStation = false;
          alert(
            'An error has occurred and the station data being shown may not be accurate.'
          );
          state.currentStnReqId = undefined;
        }
      });
  },
});

export const { updateMapData } = dataSlice.actions;
export default dataSlice.reducer;
